import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { Routes, Route, useParams, Link, useSearchParams } from 'react-router-dom';
import Catalog from './Catalog';
import Home from './Home';
import ItemDetail from './ItemDetail';
import EndOfTour from './EndOfTour';
import BootSplash from './BootSplash.jsx';
import SwipeTutorial from './SwipeTutorial.jsx';
import { tourTarget, isReturnTrip } from './tour-navigation';
import { assetFor, loadMuseum, visibleState } from './catalog';
import { designFor } from './product-designs';
import { TRAVEL_MS, REDUCED_TRAVEL_MS, makeRooms, swipeDirection, createWheelGate } from './journey';
import { playWalkSequence, unlockWalkAudio } from './museum-walk.js';
const ExhibitionSpace = lazy(() => import('./ExhibitionSpace'));
function useReducedMotion() {
  const [reduced,setReduced]=useState(()=>window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(()=>{const query=window.matchMedia('(prefers-reduced-motion: reduce)');const update=()=>setReduced(query.matches);
    query.addEventListener('change',update);return ()=>query.removeEventListener('change',update);},[]);
  return reduced;
}
const price=product=>!visibleState(product).buyable||product.price==null?'COMING SOON':`¥${Number(product.price).toLocaleString('ja-JP')}`;
const pad=n=>String(n).padStart(2,'0');
function ProductCopy({product,phase,moving,onOpen}) {
  const parts=product.name.split(' ');
  return <section className={`exhibit-copy copy-${phase}`} aria-hidden={phase==='out'}>
    <div className="copy-mask"><div className="copy-line copy-drop">{product.drop?.name||'DROP'}</div></div>
    <h2 className="exhibit-name" aria-label={product.name}>
      <span className="copy-mask"><span className="copy-line copy-name-a">{parts[0]}</span></span>
      <span className="copy-mask"><span className="copy-line copy-name-b">{parts.slice(1).join(' ')}</span></span>
    </h2>
    <div className="copy-mask"><div className="copy-line copy-commerce">
      <div><strong>{price(product)}</strong><span className="sale-state">{visibleState(product).label}</span></div>
      <button className="view-link" onClick={()=>onOpen(product)} disabled={moving||phase==='out'} tabIndex={phase==='out'?-1:0}>VIEW <span aria-hidden="true">↗</span></button>
    </div></div>
  </section>;
}
function Detail({product,onClose}) {
  const dialog=useRef(null);
  useEffect(()=>{const previous=document.activeElement;dialog.current.showModal();return ()=>previous?.focus?.();},[]);
  const status=visibleState(product),image=designFor(product).photo||assetFor(product,['main','mockup','exhibition']);
  const buyable=status.buyable&&!!product.purchase_url;
  return <dialog className="detail" ref={dialog} onCancel={e=>{e.preventDefault();onClose();}} aria-labelledby="detail-title">
    <button className="close" onClick={onClose} aria-label="Close product details" autoFocus>×</button>
    <div className="detail-image">{image&&<img src={image} alt={product.name}/>}</div>
    <div className="detail-copy"><div className="kicker">{product.drop?.name||'PARALYZE AREA'}</div>
      <h2 id="detail-title">{product.name}</h2>{product.short_description&&<p>{product.short_description}</p>}
      <div className="detail-price">{price(product)}</div>
      {buyable?<a className="buy" href={product.purchase_url}>BUY ↗</a>:<button className="buy" disabled>{status.buyable?'COMING SOON':status.label}</button>}
    </div>
  </dialog>;
}
function MuseumContent({onReady}) {
  const {slug}=useParams();const [search]=useSearchParams();
  const [data,setData]=useState({exhibition:null,products:[]}),[loading,setLoading]=useState(true),[error,setError]=useState('');
  const [selected,setSelected]=useState(null),[moving,setMoving]=useState(false);
  const [tutorialVisible,setTutorialVisible]=useState(true);
  const [trip,setTrip]=useState({from:-1,to:-1,startedAt:0,duration:0,serial:0,loop:false});
  const position=useRef(-1),locked=useRef(false),root=useRef(null),pointer=useRef(null);
  const reduced=useReducedMotion(),rooms=useMemo(()=>makeRooms(data.products),[data.products]);
  const dismissTutorial=useCallback(()=>setTutorialVisible(false),[]);
  useEffect(()=>{
    let live=true;setLoading(true);setError('');
    loadMuseum(slug).then(value=>{
      if(!live)return;setData(value);
      const requested=value.products.findIndex(p=>p.slug===search.get('exhibit'));
      position.current=requested;locked.current=false;setMoving(false);
      setTrip(t=>({from:requested,to:requested,startedAt:performance.now(),duration:0,serial:t.serial+1,loop:false}));
    }).catch(e=>{if(live)setError(e.message);}).finally(()=>{if(live)setLoading(false);});
    return ()=>{live=false;};
  },[slug,search]);
  useEffect(()=>{if(error||!loading&&!rooms.length)onReady();},[error,loading,rooms.length,onReady]);
  const go=useCallback(direction=>{
    if(locked.current||selected||!rooms.length||document.querySelector('.brand-boot'))return;
    const from=position.current,to=tourTarget(from,direction,rooms.length);if(from===to)return;
    if(from<0&&to>=0)dismissTutorial();
    const duration=reduced?REDUCED_TRAVEL_MS:TRAVEL_MS;
    playWalkSequence(duration);
    locked.current=true;position.current=to;setMoving(true);
    setTrip(t=>({from,to,startedAt:performance.now(),duration,serial:t.serial+1,loop:isReturnTrip(from,to,rooms.length)}));
  },[rooms.length,selected,reduced,dismissTutorial]);
  const settled=useCallback(index=>{locked.current=false;setMoving(false);if(index<0)setTutorialVisible(true);onReady();},[onReady]);
  useEffect(()=>{
    const element=root.current;if(!element)return;const wheelGate=createWheelGate();
    const onWheel=e=>{if(selected||e.ctrlKey||Math.abs(e.deltaX)>Math.abs(e.deltaY))return;
      e.preventDefault();const multiplier=e.deltaMode===1?16:e.deltaMode===2?element.clientHeight:1;
      const direction=wheelGate(e.deltaY*multiplier,performance.now());if(direction)go(direction);};
    const onKey=e=>{if(selected||e.repeat||e.altKey||e.ctrlKey||e.metaKey||e.target.closest('button,a,input,select,textarea,dialog'))return;
      const direction=['ArrowDown','ArrowRight','PageDown',' '].includes(e.key)?1:['ArrowUp','ArrowLeft','PageUp'].includes(e.key)?-1:0;
      if(direction){e.preventDefault();go(direction);}};
    element.addEventListener('wheel',onWheel,{passive:false});window.addEventListener('keydown',onKey);
    return ()=>{element.removeEventListener('wheel',onWheel);window.removeEventListener('keydown',onKey);};
  },[go,selected,loading]);
  const onPointerDown=e=>{
    if(e.isPrimary&&e.button===0)unlockWalkAudio();
    if(!e.isPrimary||e.button!==0||locked.current||selected||e.target.closest('button,a,dialog')){pointer.current=null;return;}
    pointer.current={x:e.clientX,y:e.clientY,id:e.pointerId};e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerUp=e=>{const start=pointer.current;pointer.current=null;if(!start||start.id!==e.pointerId)return;
    const direction=swipeDirection(start,{x:e.clientX,y:e.clientY});if(direction)go(direction);};
  if(loading)return <div className="loading" aria-hidden="true"/>;
  if(error)return <div className="loading">AREA OFFLINE<button onClick={()=>location.reload()}>RETRY ↗</button></div>;
  if(!data.exhibition||!rooms.length)return <div className="loading">COMING SOON<Link to="/catalog">WEB CATALOG ↗</Link></div>;
  const entered=trip.to>=0,atEnd=entered&&trip.to===rooms.length-1;
  const entryDeparting=entered&&moving&&trip.from<0&&!trip.loop;
  return <main ref={root} className={`museum ${entered?'is-entered':'is-entrance'} ${moving?'is-travelling':'is-settled'} ${reduced?'reduced-motion':''}`}
    data-active={trip.to} data-moving={moving} data-motion-trip={trip.from!==trip.to} data-loop={Boolean(trip.loop&&moving)}
    onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={()=>{pointer.current=null;}}>
    <header className="museum-header">
      <Link className="brand" to="/" aria-label="PARALYZE AREA home"><img src="/brand/scorpion-symbol.svg" alt=""/><span>PARALYZE AREA</span></Link>
      <Link className="museum-catalog-link" to="/catalog">WEB CATALOG <span aria-hidden="true">↗</span></Link>
    </header>
    <Suspense fallback={null}><ExhibitionSpace rooms={rooms} trip={trip} reduced={reduced} paused={!!selected} onSettled={settled}/></Suspense>
    <div className="cinema-shade" aria-hidden="true"/>
    {(!entered&&!moving||entryDeparting)&&<section className={`entry-copy ${entryDeparting?'entry-departing':''}`} aria-hidden={entryDeparting}>
      <div className="entry-label">{data.exhibition.name} <span>DROP 001</span></div>
      <h1>PARALYZE<br/><span>AREA</span></h1>
      <button className="enter-link" onClick={()=>go(1)} disabled={moving}>ENTER <span aria-hidden="true">↗</span></button>
      <span className="entry-swipe">SWIPE ↑</span>
    </section>}
    <SwipeTutorial visible={!entered&&!moving&&tutorialVisible}/>
    {entered&&<>
      <div className="room-caption" aria-hidden="true"><span>{data.exhibition.name}</span><span>{pad(trip.to+1)} / {pad(rooms.length)}</span></div>
      <div key={trip.serial} className="editorial-layer">
        {trip.from>=0&&trip.from!==trip.to&&moving&&<ProductCopy product={rooms[trip.from].product} phase="out" moving onOpen={setSelected}/>}
        <ProductCopy product={rooms[trip.to].product} phase="in" moving={moving} onOpen={setSelected}/>
      </div>
      <nav className={`journey-nav ${atEnd?'is-finale':''}`} aria-label="Exhibition navigation">
        {atEnd?<EndOfTour moving={moving} onReturn={()=>go(1)}/>:<>
          <span className="journey-count">{pad(trip.to+1)}<span> / {pad(rooms.length)}</span></span>
          <div className="journey-track" aria-hidden="true">{rooms.map((room,i)=><i key={room.product.id} className={i<=trip.to?'passed':''}/>)}</div>
          <span className="swipe-cue">SWIPE ↑</span>
        </>}
        <div className="journey-arrows"><button onClick={()=>go(-1)} disabled={moving||trip.to===0} aria-label="Previous exhibit">↑</button>
          {!atEnd&&<button onClick={()=>go(1)} disabled={moving} aria-label="Next exhibit">↓</button>}
        </div>
      </nav>
      <span className="sr-only" role="status" aria-live="polite">{!moving&&`${trip.to+1} / ${rooms.length}. ${rooms[trip.to].product.name}. ${price(rooms[trip.to].product)}${atEnd?'。展示はここまでです。もう一度上へスワイプすると入口へ戻ります。':''}`}</span>
    </>}
    {trip.loop&&moving&&!reduced&&<div className="tour-loop-curtain" aria-hidden="true"><img src="/brand/scorpion-symbol.svg" alt=""/><span>また、ひとまわり。</span></div>}
    {selected&&<Detail product={selected} onClose={()=>setSelected(null)}/>}
  </main>;
}
function Museum(){
  const [ready,setReady]=useState(false),reduced=useReducedMotion();
  const onReady=useCallback(()=>setReady(true),[]);
  return <><MuseumContent onReady={onReady}/><BootSplash ready={ready} reduced={reduced}/></>;
}
function Director(){useEffect(()=>{location.href='https://jfaeqbyxsolqnrhbnrnt.supabase.co/functions/v1/director-console';},[]);return <div className="loading">DIRECTOR CONSOLE</div>;}
export default function App(){return <Routes>
  <Route path="/" element={<Home/>}/><Route path="/museum" element={<Museum/>}/><Route path="/area/:slug" element={<Museum/>}/><Route path="/catalog" element={<Catalog/>}/><Route path="/items/:slug" element={<ItemDetail/>}/><Route path="/admin" element={<Director/>}/>
</Routes>;}
